import { AxiosResponseHeaders } from 'axios';
import axios from '../../config/axiosConfig';
import { ORIGIN, DOMAIN } from '../../shared';
import store from '../store';
import saveAs from 'file-saver';
import { all, call, cancelled as isCancelled, put, race, take, takeLatest } from 'redux-saga/effects';
import { loadHTMLPreviewSuccess, loadHTMLPreviewFailure, loadTabularDataFailure, loadTabularDataSuccess, loadQDSearchFailure, loadQDSearchSuccess, loadDownloadSuccess, loadDownloadFailure, loadDownloadProgress, loadDownloadCancel } from './queryResults.actions';
import { DownloadRequest, DownloadResponse, HTMLPreviewRequest, HTMLPreviewResponse, QueryResultsActionTypes, TabularRequest, TabularResponsePayload } from './models';
import { DOWNLOAD_FORM } from './models/queryResults.types';

type NdjsonMessage =
  | { type: 'progress'; completed: number; total: number }
  | { type: 'complete'; contentType: string; data: string }
  | { type: 'error'; message: string; callStack?: string };

/**
 * Streams NDJSON progress/result records from a single POST request,
 * dispatching progress updates as they arrive.
 */
async function fetchDownloadStream(
  payload: DownloadRequest,
  onProgress: (completed: number, total: number) => void,
  signal: AbortSignal
): Promise<NdjsonMessage> {
  const url = [ORIGIN, DOMAIN, 'api/downloads/create'].filter(Boolean).join('/');
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok || !response.body) {
    throw new Error(`Download request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalMessage: NdjsonMessage | null = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    let newlineIndex: number;

    while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);

      if (!line) {
        continue;
      }

      const message = JSON.parse(line) as NdjsonMessage;
      if (message.type === 'progress') {
        onProgress(message.completed, message.total);
      } else {
        finalMessage = message;
      }
    }
  }

  if (!finalMessage) {
    throw new Error('Download stream ended without a result.');
  }

  return finalMessage;
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

/**
 * Streams progress for the download from a single POST, then saves
 * or post-processes the resulting file once the stream completes.
 */
export function* loadDownloadStartAsyncSaga({payload}: {payload: DownloadRequest, type: string}): Generator {
  const abortController = new AbortController();

  try {
    const { finalMessage, cancelled } = (yield race({
      finalMessage: call(
        fetchDownloadStream,
        payload,
        (completed: number, total: number) => {
          store.dispatch(loadDownloadProgress({ completed, total }));
        },
        abortController.signal
      ),
      cancelled: take(loadDownloadCancel)
    })) as { finalMessage?: NdjsonMessage; cancelled?: ReturnType<typeof loadDownloadCancel> };

    if (cancelled) {
      abortController.abort();
      return;
    }

    if (!finalMessage) {
      yield put(loadDownloadFailure('Unexpected download response.'));
      return;
    }

    if (finalMessage.type === 'error') {
      yield put(loadDownloadFailure(finalMessage.message));
      return;
    }

    if (finalMessage.type !== 'complete') {
      yield put(loadDownloadFailure('Unexpected download response.'));
      return;
    }

    const completeMessage: { type: 'complete'; contentType: string; data: string } = finalMessage;
    const fileName = 'The NAEP Questions Tool - Download';
    const fileType = { type: completeMessage.contentType } as BlobPropertyBag;
    const blob = base64ToBlob(completeMessage.data, completeMessage.contentType);

    // Special Case for HTML - add styling (since we're using sass) and resolve images
    if (payload.Downloads === DOWNLOAD_FORM.HTML) {
      const response: DownloadResponse = { blob, headers: { 'content-type': completeMessage.contentType } as AxiosResponseHeaders };
      yield call(downloadHTMLStartSaga, response, fileName, fileType);
    } else {
      saveAs(blob, fileName);
      yield put(loadDownloadSuccess());
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }
    const message = typeof error === 'string'
      ? error
      : (error as {errMsg?: string; message?: string})?.errMsg ?? (error as {message?: string})?.message;
    yield put(loadDownloadFailure(message ?? error));
  } finally {
    // If takeLatest cancels this task in favor of a newer download request,
    // abort the underlying fetch so the server stops generating the file.
    if (yield isCancelled()) {
      abortController.abort();
    }
  }
}

/**
 *
 * @param response {blob: Blob, headers: AxioResponseHeaders}
 * @param fileName: string - any name
 * @param fileType: string - {type: file/type}
 */
export function* downloadHTMLStartSaga(response: {blob: Blob; headers: AxiosResponseHeaders}, fileName: string, fileType: BlobPropertyBag): Generator {
  let blobContent = '';
  const styles: string[] = [];
  yield response.blob.text().then( data => {blobContent = data;});

  // grab all style tags on page
  document.querySelectorAll('style').forEach(el => styles.push(el.outerHTML));

  const nodeList = document.querySelectorAll('link[rel=stylesheet]') as NodeListOf<HTMLLinkElement>;

  const styleSheets = [].slice.call(nodeList) as HTMLLinkElement[];

  for (let i = 0; i < styleSheets.length; i++) {
    yield call(getBlobAsync, styleSheets[i], styles);
  }

  // create generic element inorder to add bootstrap classes
  const div = document.createElement('div');
  div.className = 'container';
  div.innerHTML = blobContent;

  // grab all img tags
  const imgNodeList = div.querySelectorAll('img');

  const imgs = [].slice.call(imgNodeList) as HTMLImageElement[];

  // if imgs - resolve them and download on last resolve
  if (imgs && imgs.length) {
    for (let i = 0; i < imgs.length; i++) {
      yield call( getImageAsync, imgs[i], styles);
    }
  }
  // inject only styles if no imgs OR wait for take to receive last emit
  const blob = new Blob([...styles, div.outerHTML], fileType);
  saveAs(blob, fileName);
  yield put(loadDownloadSuccess());
}

/**
 * Grabs linked files and resolves them. Trigger emits styles: strings[]
 * @param el
 * @param index
 * @param length
 * @param styles
 * @param emit
 * @returns Promise<string[]|void>
 */
export async function getBlobAsync(el: HTMLLinkElement, styles: string[]): Promise<unknown> {
  try {
    const data =  await axios.get(el.href, {responseType: 'blob'})
      .then((res) => res.data);

    return promiseFileReader(data, styles);
  } catch (err){
    return new Promise((_, reject) => {
      reject(err);
    });
  }
}

export async function getImageAsync(el: HTMLImageElement, styles: string[]): Promise<unknown> {
  try {
    const data =  await fetch(el.src, {
      method: 'GET',
      mode: 'no-cors'
    })
      .then(res => res.blob());

    return promiseFileReader(data, styles);
  } catch (err){
    return new Promise((_, reject) => {
      reject(err);
    });
  }
}

/**
 * wraps FileReader in a promise
 * @param data
 * @param styleArr
 * @returns
 */
async function promiseFileReader(data: Blob, styleArr: string[]) {
  const promise = new Promise((resolve, reject) => {
    const reader = new FileReader;

    reader.onloadend = () => {
      const styleEl = document.createElement('style');
      if (typeof reader?.result === 'string') {
        styleEl.innerHTML = reader.result;
      }
      styleArr.push(styleEl.outerHTML);
      resolve(styleArr);
    };

    reader.onerror = (err) => {
      reject(err);
    };

    reader.readAsText(data, 'utf-8');

  });
  return promise;
}

/**
 * Fetches question view result from db,
 * then dispatches an action with the corresponding result
 */
export function* loadHTMLPreviewAsyncSaga(payload: HTMLPreviewRequest): Generator {
  try {
    const response = yield axios.post<HTMLPreviewResponse>('api/queryresults/getHTMLPreview', {
      ...payload
    })
      .then((res) => res.data);
    yield put(loadHTMLPreviewSuccess(<HTMLPreviewResponse>response));
  } catch (error) {
    yield put(loadHTMLPreviewFailure(error));
  }
}

/**
 * Fetches question view result from db,
 * then dispatches an action with the corresponding result
 */
export function* loadHTMLPreviewAsyncStartSaga({payload}: {payload: HTMLPreviewRequest; type: string}): Generator {
  try {
    const response = yield axios.post<HTMLPreviewResponse>('api/queryresults/getHTMLPreview', {
      ...payload
    })
      .then((res) => res.data);
    yield put(loadHTMLPreviewSuccess(<HTMLPreviewResponse>response));
  } catch (error) {
    yield put(loadHTMLPreviewFailure(error));
  }
}

/**
 * Fetches tablist view result from db,
 * then dispatches an action with the corresponding result
 */
export function* loadTabularDataAsyncSaga({payload}: {payload: TabularRequest, type: string}): Generator {
  try {
    const response = yield axios.post<TabularResponsePayload>('api/queryresults/getTabular', {
      ...payload
    })
      .then((res): TabularResponsePayload => ({...res.data, itemRequest: payload}));
    yield put(loadTabularDataSuccess(<TabularResponsePayload>response));
  } catch (error) {
    yield put(loadTabularDataFailure(error));
  }
}

/**
 * Fetches tablist view result filtered from db,
 * then dispatches an action with the corresponding result
 */
export function* loadQDSearchStartAsyncSaga({payload}: {payload: TabularRequest, type: string}): Generator {
  try {
    const response = yield axios.post<TabularResponsePayload>('api/queryresults/getTabular', {
      ...payload
    })
      .then((res) => ({...res.data, itemRequest: payload}));
    yield put(loadQDSearchSuccess(<TabularResponsePayload>response));
  } catch (error) {
    yield put(loadQDSearchFailure(error));
  }
}

/**
 * Listens for LOAD_QUERY_RESULTS_START action, dispatchs loadTabularDataAsyncSaga
 * and loadHTMLPreviewAsyncSaga
 */
export function* loadQueryResultsStartSaga(): Generator {
  yield takeLatest(QueryResultsActionTypes.LOAD_QUERY_RESULTS_START, loadTabularDataAsyncSaga);
}

/**
 * Listens for LOAD_HTML_PREVIEW_START, dispatches loadHTMLPreviewAsyncStartSaga
 */
export function* loadHTMLPreviewStartSaga(): Generator {
  yield takeLatest(QueryResultsActionTypes.LOAD_HTML_PREVIEW_START, loadHTMLPreviewAsyncStartSaga);
}

/**
 * Listens for LOAD_QDSEARCH_START action, dispatchs loadQDSearchStartAsyncSaga, which in turn dispathes
 * loadTabularDataAsyncSaga and loadHTMLPreviewAsyncSaga
 */
export function* loadQDSearchStartSaga(): Generator {
  yield takeLatest(QueryResultsActionTypes.LOAD_QDSEARCH_START, loadQDSearchStartAsyncSaga);
}

/**
 * Listens for LOAD_DOWNLOAD_START action, dispatchs loadDownloadStartAsyncSaga
 */
export function* loadDownloadStartSaga(): Generator {
  yield takeLatest(QueryResultsActionTypes.LOAD_DOWNLOAD_START, loadDownloadStartAsyncSaga);
}

/**
 * Composite of all queryResults sagas that are listening for external actions
 */
export function* queryResultsSagas(): Generator {
  yield all([
    call(loadQueryResultsStartSaga),
    call(loadQDSearchStartSaga),
    call(loadDownloadStartSaga),
    call(loadHTMLPreviewStartSaga)
  ]);
}

export default queryResultsSagas;